/**
 * User Context
 *
 * Delegates to AuthContext for real auth state. Maintains the existing
 * useUser / useCurrentUserId / useCurrentUserInfo API so all existing
 * consumers keep working without changes.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface UserContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const auth = useAuth();

  const value: UserContextValue = useMemo(() => {
    if (auth.isLoading) {
      return { user: null, isLoading: true, isAuthenticated: false };
    }

    if (!auth.isAuthenticated || !auth.user) {
      return { user: null, isLoading: false, isAuthenticated: false };
    }

    return {
      user: {
        id: auth.user.id,
        name: auth.user.name,
        email: auth.user.email,
        avatar: undefined,
      },
      isLoading: false,
      isAuthenticated: true,
    };
  }, [auth.isLoading, auth.isAuthenticated, auth.user]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

/**
 * Hook to get the current user ID with a fallback
 * Useful for places that need just the user ID
 */
export function useCurrentUserId(): string {
  const { user } = useUser();
  return user?.id || 'anonymous';
}

/**
 * Hook to get the current user for creating records
 * Returns user info suitable for API calls
 */
export function useCurrentUserInfo(): { id: string; name: string; avatar: string } {
  const { user } = useUser();
  return {
    id: user?.id || 'anonymous',
    name: user?.name || 'Anonymous User',
    avatar: user?.avatar || '',
  };
}
